/**
 * @zendir/ui - Popover & Menu Components
 *
 * General-purpose popover for dropdown menus, sort controls, action menus,
 * and contextual information. Portal-rendered with auto-positioning.
 *
 * Astro UX Compliance:
 * - Consistent with Dialog/Tooltip styling
 * - Focus trap for keyboard navigation
 * - Escape to close
 * - Click outside to close
 * - Reduced motion support
 *
 * @example
 * ```tsx
 * <Popover
 *   trigger={<Button variant="secondary">Sort</Button>}
 *   placement="bottom-start"
 * >
 *   <Menu>
 *     <Menu.Item onClick={...}>Sort by Time</Menu.Item>
 *     <Menu.Item onClick={...}>Sort by Name</Menu.Item>
 *     <Menu.Divider />
 *     <Menu.Item onClick={...} destructive>Clear All</Menu.Item>
 *   </Menu>
 * </Popover>
 * ```
 */

import React, {
  memo,
  useState,
  useRef,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import { useTheme } from "../../theme";

// ─── Popover ─────────────────────────────────────────────────────────────────

export type PopoverPlacement =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

export interface PopoverProps {
  /** Trigger element */
  trigger: React.ReactElement;
  /** Popover content */
  children: React.ReactNode;
  /** Placement relative to trigger */
  placement?: PopoverPlacement;
  /** Open on hover instead of click */
  hover?: boolean;
  /** Controlled open state */
  open?: boolean;
  /** Controlled open change */
  onOpenChange?: (open: boolean) => void;
  /** Close on content click */
  closeOnClick?: boolean;
  /** Width: 'trigger' matches trigger width, number for px, 'auto' */
  width?: "trigger" | "auto" | number;
  /** Custom style for popover panel */
  style?: React.CSSProperties;
}

const PopoverContext = createContext<{ close: () => void }>({
  close: () => {},
});

export const Popover = memo(function Popover({
  trigger,
  children,
  placement = "bottom-start",
  hover = false,
  open: controlledOpen,
  onOpenChange,
  closeOnClick = true,
  width = "auto",
  style,
}: PopoverProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [internalOpen, setInternalOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (val: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(val);
      onOpenChange?.(val);
    },
    [controlledOpen, onOpenChange],
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  // Click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, close]);

  // Escape key + cleanup hoverTimeout on unmount
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, close]);

  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeout.current);
    };
  }, []);

  // Position calculation — `positioned` gates visibility so the panel is never
  // painted in document flow before the first RAF resolves coordinates.
  // Initializing with position: 'fixed' ensures that the hidden first-frame
  // measurement doesn't get constrained by the trigger's parent container.
  const [position, setPosition] = useState<React.CSSProperties>({
    position: "fixed",
    left: 0,
    top: 0,
  });
  const [positioned, setPositioned] = useState(false);

  // Reset visibility guard whenever the popover closes so the next open starts
  // hidden again rather than flashing the stale coordinates for one frame.
  useEffect(() => {
    if (!isOpen) setPositioned(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const gap = 8;
    const viewportPadding = 8;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const panelWidth =
        width === "trigger"
          ? rect.width
          : typeof width === "number"
            ? width
            : (popoverRef.current?.offsetWidth ?? 220);
      const panelHeight = popoverRef.current?.offsetHeight ?? 220;

      let left = rect.left;
      let top = rect.bottom + gap;

      if (placement.startsWith("top")) {
        top = rect.top - panelHeight - gap;
      } else if (placement.startsWith("bottom")) {
        top = rect.bottom + gap;
      } else if (placement.startsWith("left")) {
        left = rect.left - panelWidth - gap;
      } else if (placement.startsWith("right")) {
        left = rect.right + gap;
      }

      if (placement.startsWith("top") || placement.startsWith("bottom")) {
        if (placement.endsWith("-start")) {
          left = rect.left;
        } else if (placement.endsWith("-end")) {
          left = rect.right - panelWidth;
        } else {
          left = rect.left + (rect.width - panelWidth) / 2;
        }
      } else {
        if (placement.endsWith("-start")) {
          top = rect.top;
        } else if (placement.endsWith("-end")) {
          top = rect.bottom - panelHeight;
        } else {
          top = rect.top + (rect.height - panelHeight) / 2;
        }
      }

      left = Math.max(
        viewportPadding,
        Math.min(left, window.innerWidth - panelWidth - viewportPadding),
      );
      top = Math.max(
        viewportPadding,
        Math.min(top, window.innerHeight - panelHeight - viewportPadding),
      );

      const pos: React.CSSProperties = {
        position: "fixed",
        zIndex: 9990,
        left,
        top,
      };

      if (width === "trigger") {
        pos.width = rect.width;
        pos.minWidth = rect.width;
      } else if (typeof width === "number") {
        pos.width = width;
      }

      setPosition(pos);
      setPositioned(true);
    };

    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, placement, width]);

  const triggerProps: Record<string, (() => void) | undefined> = {};
  if (hover) {
    triggerProps.onMouseEnter = () => {
      clearTimeout(hoverTimeout.current);
      setOpen(true);
    };
    triggerProps.onMouseLeave = () => {
      hoverTimeout.current = setTimeout(close, 150);
    };
  } else {
    triggerProps.onClick = () => setOpen(!isOpen);
  }

  const popoverHoverProps = hover
    ? {
        onMouseEnter: () => clearTimeout(hoverTimeout.current),
        onMouseLeave: () => {
          hoverTimeout.current = setTimeout(close, 150);
        },
      }
    : {};

  return (
    <PopoverContext.Provider value={{ close }}>
      <div
        ref={triggerRef}
        style={{ display: "inline-flex" }}
        aria-expanded={isOpen}
        aria-haspopup="true"
        {...triggerProps}
      >
        {trigger}
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          role="dialog"
          onClick={closeOnClick ? close : undefined}
          {...popoverHoverProps}
          style={{
            ...position,
            visibility: positioned ? "visible" : "hidden",
            backgroundColor: isTransparentTheme
              ? "rgba(15, 12, 30, 0.85)"
              : tokens.colors.background.elevated,
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.md,
            boxShadow: tokens.shadows.lg,
            padding: tokens.spacing.xs,
            maxWidth: "min(320px, calc(100vw - 16px))",
            fontFamily: tokens.typography.fontFamily.primary,
            animation: `zendir-popover-enter 150ms ${tokens.animation.easing.default}`,
            ...(isTransparentTheme
              ? {
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                }
              : {}),
            ...style,
          }}
        >
          {children}
        </div>
      )}

      <style>{`
        @keyframes zendir-popover-enter {
          from { opacity: 0; transform: translateY(-4px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </PopoverContext.Provider>
  );
});

// ─── Menu ────────────────────────────────────────────────────────────────────

export interface MenuProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const MenuRoot = memo(function Menu({
  children,
  style,
}: MenuProps): React.ReactElement {
  return (
    <div
      role="menu"
      style={{ minWidth: "min(160px, calc(100vw - 32px))", ...style }}
    >
      {children}
    </div>
  );
});

// ─── Menu.Item ───────────────────────────────────────────────────────────────

export interface MenuItemProps {
  /** Click handler */
  onClick?: () => void;
  /** Icon (ReactNode or IconName string) */
  icon?: React.ReactNode;
  /** Label text */
  children: React.ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Destructive action (red text) */
  destructive?: boolean;
  /** Keyboard shortcut hint */
  shortcut?: string;
}

const MenuItem = memo(function MenuItem({
  onClick,
  icon,
  children,
  disabled = false,
  destructive = false,
  shortcut,
}: MenuItemProps): React.ReactElement {
  const { tokens } = useTheme();
  const { close } = useContext(PopoverContext);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(() => {
    if (disabled) return;
    onClick?.();
    close();
  }, [disabled, onClick, close]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const item = e.currentTarget;
    const siblings = item.parentElement?.querySelectorAll(
      '[role="menuitem"]:not([disabled])',
    );
    if (!siblings) return;
    const items = Array.from(siblings) as HTMLElement[];
    const idx = items.indexOf(item as HTMLElement);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  }, []);

  const color = destructive
    ? tokens.colors.status.critical
    : disabled
      ? tokens.colors.text.tertiary
      : tokens.colors.text.primary;

  return (
    <button
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: tokens.spacing.sm,
        width: "100%",
        padding: `${tokens.spacing.sm} ${tokens.spacing.sm}`,
        minHeight: 44,
        border: "none",
        background:
          isHovered && !disabled
            ? `${destructive ? tokens.colors.status.critical : tokens.colors.accent.primary}12`
            : "transparent",
        color,
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: tokens.typography.fontWeight.normal,
        fontFamily: tokens.typography.fontFamily.primary,
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: tokens.borderRadius.sm,
        textAlign: "left",
        transition: tokens.animation.fast,
        opacity: disabled ? 0.5 : 1,
        outline: "none",
      }}
    >
      {icon && <span style={{ flexShrink: 0, display: "flex" }}>{icon}</span>}
      <span style={{ flex: 1 }}>{children}</span>
      {shortcut && (
        <span
          style={{
            fontSize: tokens.typography.fontSize.xxs,
            color: tokens.colors.text.tertiary,
            fontFamily: tokens.typography.fontFamily.mono,
            flexShrink: 0,
          }}
        >
          {shortcut}
        </span>
      )}
    </button>
  );
});

// ─── Menu.Divider ────────────────────────────────────────────────────────────

const MenuDivider = memo(function MenuDivider(): React.ReactElement {
  const { tokens } = useTheme();
  return (
    <div
      role="separator"
      style={{
        height: "1px",
        backgroundColor: tokens.colors.border.muted,
        margin: `${tokens.spacing.xs} 0`,
      }}
    />
  );
});

// ─── Menu.Label ──────────────────────────────────────────────────────────────

const MenuLabel = memo(function MenuLabel({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { tokens } = useTheme();
  return (
    <div
      style={{
        padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
        fontSize: tokens.typography.fontSize.xxs,
        fontWeight: tokens.typography.fontWeight.bold,
        color: tokens.colors.text.tertiary,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}
    >
      {children}
    </div>
  );
});

// ─── Compound Export ─────────────────────────────────────────────────────────

type MenuComponent = typeof MenuRoot & {
  Item: typeof MenuItem;
  Divider: typeof MenuDivider;
  Label: typeof MenuLabel;
};

export const Menu: MenuComponent = Object.assign(MenuRoot, {
  Item: MenuItem,
  Divider: MenuDivider,
  Label: MenuLabel,
});

export default Popover;
